
import React, { Component } from 'react';
import { connect } from 'react-redux';
import assign from 'lodash/assign';
import get from 'lodash/get';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';

import {
    sagas as sagasMap,
    MapInContainer,
    getLayerIdFromFeatureForData,
    getToolsMode
} from '../../map';
import appGetActions from '../../app/saga';
import { sagas as sagasData, DataWindow } from '../../data';
import { sagas as sagasMeasurement } from '../../measurement';
import { getMapLayers } from '../../project';
import TabPanelsViewer from './TabPanelsViewer';
import getAppModules from './getAppModules';
import getTabsModule from './getTabsModule';
import getWindowList from './getWindowList';
import showObjectsInfo from './showObjectsInfo';
import WindowSwitcher from './WindowSwitcher';

/**
 * Viewer containing 2D map window and panel with tools.
 *
 * @reactProps {Object} projectDefinition - project definition
 * @reactProps {Object} appLayoutProps - properties for customize layout
 * @reactProps {Object} map - actual map state
 */
class MapViewer extends Component {

    classNames = mergeStyleSets({
        root: {

        },
        mapView: {
            borderLeft: '1px solid #cccccc',
            borderRight: '1px solid #cccccc'
        }
    });

    getAppModules() {
        return getAppModules(this.props.projectDefinition);
    }

    getAvailablePanels() {
        const panels = ['data', 'layer', 'measurement', 'print', 'search', 'share', 'user'];
        const hasHomeContent = get(this.props, 'projectDefinition.app.home.content');
        return hasHomeContent ? ['home', ...panels] : panels;
    }

    getLayout() {
        let layout = get(this.props, 'projectDefinition.app.layout', null);
        if (!layout) {
            layout = get(this.props, 'layout', {});
        }
        assign(layout, this.props.appLayoutProps);
        return layout;
    }

    getLayoutValue(name, defaultValue) {
        let layout = this.getLayout();
        return get(layout, name, defaultValue);
    }

    getLayoutValues() {
        return {
            layersPanelPosition: this.getLayoutValue('layersPanelPosition', 'left'),
            tabPanelsWidth: this.getLayoutValue('tabPanelsWidth', '300px')
        }
    }

    getTabs() {
        return getTabsModule(
            this.getAppModules(),
            this.getAvailablePanels(),
            !!this.props.map,
            (this.props.auth.data && this.props.auth.data.user)
        );
    }

    getWindowSwitcher() {
        const windowList = getWindowList(this.getAppModules());

        return (
            <WindowSwitcher
                activeWindow="map"
                appActions={this.props.appActions}
                windowList={windowList}
            />
        );
    }

    handleChangeView = (center, zoom, rotation, resolution) => {
        this.props.mapActions.changeView(center, zoom, rotation, resolution);
    }

    handleFeatureClick = async (feature) => {
        let layerId = getLayerIdFromFeatureForData(feature);
        if (layerId) {
            this.handleObjectInfo(
                layerId,
                feature.getProperties()
            );
        }
    }

    handleFeaturesFind = async (features, finished, coordinates) => {
        let objects = features.map(item => {
            return {
                layerId: getLayerIdFromFeatureForData(item),
                attributes: item.getProperties()
            }
        });
        showObjectsInfo({
            appActions: this.props.appActions,
            appModules: this.getAppModules(),
            appConfig: this.props.projectDefinition.app,
            dataActions: this.props.dataActions,
            finished,
            objects,
            mapLayers: getMapLayers(this.props.project),
            coordinates
        });
    }

    handleObjectInfo = async (layerId, attributes, sourceType) => {
        showObjectsInfo({
            appActions: this.props.appActions,
            appModules: this.getAppModules(),
            appConfig: this.props.projectDefinition.app,
            dataActions: this.props.dataActions,
            finished: true,
            objects: [{
                attributes,
                layerId
            }],
            mapLayers: getMapLayers(this.props.project),
            sourceType
        });
    }

    handleShowMessage = (message) => {
        showObjectsInfo({
            appActions: this.props.appActions,
            appModules: this.getAppModules(),
            appConfig: this.props.projectDefinition.app,
            dataActions: this.props.dataActions,
            message,
        });
    }

    render() {
        return (
            <Stack horizontal verticalFill className={this.classNames.root} tokens={{childrenGap: 0, padding: 5}}>
                {this.renderContent(this.getLayoutValues())}
            </Stack>
        );
    }

    renderContent(layoutValues) {
        const activeWindow = this.props.uiValues['activeWindow'] || 'map';
        switch (activeWindow) {
            case 'data':
                return this.renderContentDataWindow(layoutValues);
            default:
                return this.renderContentMapWindow(layoutValues);
        }
    }

    renderContentDataWindow(layoutValues) {
        return (
            <Stack.Item grow>
                <DataWindow
                    appConfig={this.props.projectDefinition.app}
                    appActions={this.props.appActions}
                    tabs={this.getTabs()}
                    windowList={getWindowList(this.getAppModules())}
                />
            </Stack.Item>
        );
    }


    renderContentMapWindow(layoutValues) {
        let tabPanelsComponent = this.renderTabPanels(layoutValues);
        let mapComponent = this.renderMap();
        let leftComponent = tabPanelsComponent;
        let rightComponent = mapComponent;
        if (layoutValues['layersPanelPosition'] === 'right') {
            leftComponent = mapComponent;
            rightComponent = tabPanelsComponent;
        }
        return (
            <React.Fragment>
                {leftComponent}
                {rightComponent}
            </React.Fragment>
        );
    }

    renderMap() {
        const toolsMode = getToolsMode(this.props.map);

        return (
            <Stack.Item grow className={this.classNames.mapView}>
                <MapInContainer
                    appConfig={this.props.projectDefinition.app}
                    map={this.props.map}
                    mapLayersDefinition={getMapLayers(this.props.project)}
                    appActions={this.props.appActions}
                    mapActions={this.props.mapActions}
                    measurementActions={this.props.measurementActions}
                    toolsMode={toolsMode}
                    onChangeView={this.handleChangeView}
                    onFeatureClick={this.handleFeatureClick}
                    onFeaturesFind={this.handleFeaturesFind}
                    onShowMessage={this.handleShowMessage}
                />
            </Stack.Item>
        );
    }

    renderTabPanels(layoutValues) {
        return (
            <Stack.Item>
                <TabPanelsViewer
                    additionalContent={this.getWindowSwitcher()}
                    appActions={this.props.appActions}
                    appConfig={this.props.projectDefinition.app}
                    appModules={this.getAppModules()}
                    layoutValues={layoutValues}
                    map={this.props.map}
                    mapActions={this.props.mapActions}
                    onObjectInfo={this.handleObjectInfo}
                    tabs={this.getTabs()}
                    uiValues={this.props.uiValues}
                />
            </Stack.Item>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return {
        appActions: appGetActions(dispatch),
        dataActions: sagasData.getActions(dispatch),
        mapActions: sagasMap.getActions(dispatch),
        measurementActions: sagasMeasurement.getActions(dispatch)
    };
}

function mapStateToProps(state, ownProps) {
    return {
        ...ownProps,
        auth: state.auth,
        project: state.project,
        uiValues: state.app.uiValues
    };
}

const ConnectedMapViewer = connect(mapStateToProps, mapDispatchToProps)(MapViewer);

export default ConnectedMapViewer;
